import React from "react";
import { TextField } from "@mui/material";
import { InvoicePartySnapshot } from "./mockInvoiceData";
import GoogleAddressTextField from '../../common/GoogleAddressTextField';
import { googleAddressToInvoiceAddress } from '../../../utils/googleAddressAutocomplete';
import CountrySelectField from '../../common/dashboard/CountrySelectField';
import { formatStaffPhoneInput } from '../../../utils/memberContactFormatting';

export type InvoicePartyFieldErrors = Partial<
  Record<keyof InvoicePartySnapshot, string>
>;

export const getInvoicePartyFieldErrors = (
  formErrors: Record<string, string>,
  prefix: string
): InvoicePartyFieldErrors =>
  (Object.keys(emptyInvoicePartySnapshot()) as Array<keyof InvoicePartySnapshot>)
    .reduce<InvoicePartyFieldErrors>((errors, field) => {
      const error = formErrors[`${prefix}${field}`];

      return error ? { ...errors, [field]: error } : errors;
    }, {});

export const emptyInvoicePartySnapshot = (): InvoicePartySnapshot => ({
  customerName: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  province: "",
  postalCode: "",
  country: "Canada",
});

const fields: Array<{
  key: keyof InvoicePartySnapshot;
  label: string;
  requiredForIssuedInvoice?: boolean;
  type?: string;
}> = [
  { key: "customerName", label: "Company / customer name", requiredForIssuedInvoice: true },
  { key: "contactName", label: "Billing contact name" },
  { key: "contactEmail", label: "Billing contact email", requiredForIssuedInvoice: true, type: "email" },
  { key: "contactPhone", label: "Billing contact phone", type: "tel" },
  // The address is optional for invoice issue (approved Oct 12 2026). The
  // backend leaves missing address fields off the invoice.
  { key: "addressLine1", label: "Address line 1" },
  { key: "addressLine2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "province", label: "Province / territory" },
  { key: "postalCode", label: "Postal code" },
  { key: "country", label: "Country" },
];

export const InvoicePartyFields = ({
  disabled = false,
  errors = {},
  idPrefix,
  onChange,
  value,
}: {
  disabled?: boolean;
  errors?: InvoicePartyFieldErrors;
  idPrefix: string;
  onChange: (nextValue: InvoicePartySnapshot) => void;
  value: InvoicePartySnapshot;
}) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
    {fields.map((field) => {
      const error = errors[field.key] || "";
      const required = Boolean(field.requiredForIssuedInvoice);

      return (
        <div
          key={field.key}
          data-invoice-field-error={error ? "true" : undefined}
          className={field.key === "addressLine1" || field.key === "addressLine2" ? "md:col-span-2" : ""}
        >
          {field.key === 'addressLine1' ? <GoogleAddressTextField
            id={`${idPrefix}-${field.key}`} label={field.label} value={value.addressLine1}
            required={required} disabled={disabled} error={Boolean(error)} helperText={error || undefined}
            onValueChange={addressLine1 => onChange({ ...value, addressLine1 })}
            onAddressSelect={address => onChange({ ...value, ...googleAddressToInvoiceAddress(address) })}
            fullWidth
          /> : field.key === "country" ? <CountrySelectField
            id={`${idPrefix}-${field.key}`}
            label={field.label}
            value={value.country}
            required={required}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error || undefined}
            onValueChange={(country) => onChange({ ...value, country })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          /> : <TextField
            id={`${idPrefix}-${field.key}`}
            label={field.label}
            type={field.type}
            value={value[field.key]}
            required={required}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error || undefined}
            onChange={({ target }) =>
              onChange({ ...value, [field.key]: field.key === "contactPhone" ? formatStaffPhoneInput(target.value) : target.value })
            }
            fullWidth
          />}
        </div>
      );
    })}
  </div>
);
